/**
 * A página de um trabalho da biblioteca.
 *
 * É a tela que decide se o leitor paga ou não, e por isso ela é generosa no que é
 * aberto: resumo, abstract e todos os resumos em outros idiomas, com o nome do
 * autor, a instituição, o curso e o ano. O que fica fechado é **o texto**.
 *
 * Depois da consulta paga, o trabalho abre na mesma página, no desenho da ABNT —
 * o mesmo componente da prévia do editor, então o leitor vê exatamente o que o
 * autor via quando escreveu.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, BookOpen, Check, Loader2, Lock, Wallet } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Campo } from '@/components/ui/label';
import { DocumentoImpresso } from '@/documento/DocumentoImpresso';
import { api, ErroApi, urlDaImagem } from '@/lib/api';
import type { DocumentoAcademico } from '@miolos/core';
import {
  ROTULO_NIVEL_DA_BIBLIOTECA,
  ROTULO_USO_DE_IA,
  precoFormatado,
  type TrabalhoDaBiblioteca,
} from '@/lib/biblioteca';
import { useAuth } from '@/auth/AuthContext';

export function Trabalho() {
  const { id = '' } = useParams();
  const { usuario } = useAuth();
  const [params, setParams] = useSearchParams();

  const [dados, setDados] = useState<TrabalhoDaBiblioteca | null>(null);
  const [erro, setErro] = useState('');
  const [ocupado, setOcupado] = useState('');
  const [contribuicao, setContribuicao] = useState('');
  const [aviso, setAviso] = useState('');
  const [texto, setTexto] = useState<DocumentoAcademico | null>(null);

  const consultaPendente = params.get('consulta');

  const carregar = useCallback(async () => {
    try {
      setDados(await api.abrirTrabalho(id));
      setErro('');
    } catch (causa) {
      setDados(null);
      setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível abrir este trabalho.');
    }
  }, [id]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  /**
   * Voltou do checkout: confere o pagamento no provedor.
   *
   * São até seis tentativas, como no "Já paguei" dos planos — o Pix às vezes leva
   * alguns segundos para aparecer no extrato do provedor.
   */
  useEffect(() => {
    if (!consultaPendente || !usuario) return;
    let tentativa = 0;
    let vivo = true;

    const conferir = async () => {
      tentativa += 1;
      try {
        const resultado = await api.confirmarConsulta(consultaPendente);
        if (!vivo) return;
        if (resultado.pago) {
          setAviso('Pagamento confirmado. O trabalho está aberto abaixo.');
          const proximos = new URLSearchParams(params);
          proximos.delete('consulta');
          setParams(proximos, { replace: true });
          await carregar();
          return;
        }
      } catch {
        /* segue tentando */
      }
      if (vivo && tentativa < 6) window.setTimeout(() => void conferir(), 4000);
      else if (vivo) setAviso('Ainda não vimos o pagamento. Se o Pix já caiu, recarregue a página em instantes.');
    };

    void conferir();
    return () => {
      vivo = false;
    };
  }, [consultaPendente, usuario, carregar, params, setParams]);

  const consultar = async () => {
    if (!usuario) return;
    setOcupado('consultar');
    setErro('');
    try {
      const centavos = contribuicao.trim() ? Math.round(Number(contribuicao.replace(',', '.')) * 100) : undefined;
      const resultado = await api.consultarTrabalho(id, Number.isFinite(centavos) ? centavos : undefined);
      if ('gratuito' in resultado) {
        await carregar();
        return;
      }
      if (resultado.url) {
        window.location.href = resultado.url;
        return;
      }
      setErro('O provedor não devolveu o link de pagamento. Tente de novo.');
    } catch (causa) {
      setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível abrir a consulta.');
    } finally {
      setOcupado('');
    }
  };

  const abrirTexto = async () => {
    setOcupado('texto');
    setErro('');
    try {
      const { documento } = await api.lerTrabalho(id);
      setTexto(documento);
    } catch (causa) {
      setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível abrir o texto.');
    } finally {
      setOcupado('');
    }
  };

  if (erro && !dados) {
    return (
      <div className="mx-auto max-w-2xl space-y-4 py-8">
        <p className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">{erro}</p>
        <Button asChild variant="contorno">
          <Link to="/biblioteca">
            <ArrowLeft />
            Voltar para a biblioteca
          </Link>
        </Button>
      </div>
    );
  }

  if (!dados) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center gap-3 text-muted-foreground">
        <Loader2 className="size-5 animate-spin" />
        <span className="text-sm">Abrindo o trabalho...</span>
      </div>
    );
  }

  const { publicacao } = dados;
  const gratuito = publicacao.precoAPagar === 0;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <Link to="/biblioteca" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
        <ArrowLeft className="size-3.5" />
        Biblioteca de trabalhos
      </Link>

      <header className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tom="principal">{ROTULO_NIVEL_DA_BIBLIOTECA[publicacao.nivel].split(' (')[0]}</Badge>
          <span className="text-xs text-muted-foreground">
            {publicacao.ano} · {publicacao.instituicao}
            {publicacao.curso ? ` · ${publicacao.curso}` : ''}
          </span>
          {gratuito ? <Badge tom="sucesso">Aberto</Badge> : null}
        </div>
        <h1 className="text-2xl font-semibold leading-tight tracking-tight">{publicacao.titulo}</h1>
        {publicacao.subtitulo ? <p className="text-sm text-muted-foreground">{publicacao.subtitulo}</p> : null}
        <p className="text-sm text-muted-foreground">{publicacao.autores}</p>
      </header>

      {aviso ? (
        <p className="flex items-start gap-2 rounded-md border border-emerald-300/70 bg-emerald-50 p-3 text-sm text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-300">
          <Check className="mt-0.5 size-4 shrink-0" />
          {aviso}
        </p>
      ) : null}

      {erro ? <p className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">{erro}</p> : null}

      {/* ---------------- O resumo, que é aberto ---------------- */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Resumo</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm leading-relaxed">{publicacao.resumo}</p>
          {publicacao.palavrasChave.length > 0 ? (
            <p className="text-xs text-muted-foreground">
              <strong className="text-foreground">Palavras-chave:</strong> {publicacao.palavrasChave.join('; ')}.
            </p>
          ) : null}
        </CardContent>
      </Card>

      {publicacao.abstract ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Abstract</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm leading-relaxed">{publicacao.abstract}</p>
            {publicacao.keywords.length > 0 ? (
              <p className="text-xs text-muted-foreground">
                <strong className="text-foreground">Keywords:</strong> {publicacao.keywords.join('; ')}.
              </p>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {publicacao.resumos.map((resumo) => (
        <Card key={resumo.titulo}>
          <CardHeader>
            <CardTitle className="text-base">{resumo.titulo}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm leading-relaxed">{resumo.resumo}</p>
            {resumo.palavrasChave.length > 0 ? (
              <p className="text-xs text-muted-foreground">
                <strong className="text-foreground">Palavras-chave:</strong> {resumo.palavrasChave.join('; ')}.
              </p>
            ) : null}
          </CardContent>
        </Card>
      ))}

      {/* ---------------- O texto, fechado ---------------- */}
      {dados.acesso ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">O trabalho completo</CardTitle>
            <CardDescription>
              {dados.autor
                ? 'Você é o autor: o texto está aberto para você e para quem fizer a consulta.'
                : 'Abrir o texto é liberado para esta conta.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {texto ? (
              <div className="rounded-md border border-border bg-white p-2">
                <DocumentoImpresso documento={texto} resolverImagem={urlDaImagem} />
              </div>
            ) : (
              <Button onClick={() => void abrirTexto()} disabled={ocupado === 'texto'}>
                {ocupado === 'texto' ? <Loader2 className="animate-spin" /> : <BookOpen />}
                Abrir o trabalho completo
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Lock className="size-4" />
              O texto completo
            </CardTitle>
            <CardDescription>
              O resumo é aberto. Para ler o trabalho inteiro, faça a consulta —{' '}
              {gratuito ? 'neste caso, sem cobrança.' : `${precoFormatado(publicacao.precoAPagar)}.`}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {!usuario ? (
              <Button asChild>
                <Link to={`/entrar?voltar=/biblioteca/${publicacao.id}`}>
                  <Wallet />
                  Entrar para consultar
                </Link>
              </Button>
            ) : (
              <>
                {publicacao.contribuicaoAtiva && !gratuito ? (
                  <Campo
                    rotulo="Contribuição ao autor (opcional)"
                    dica={
                      publicacao.contribuicaoSugeridaCentavos
                        ? `O autor sugere ${precoFormatado(publicacao.contribuicaoSugeridaCentavos)}. Deixe vazio para pagar só a consulta.`
                        : 'Deixe vazio para pagar só a consulta.'
                    }
                  >
                    <input
                      value={contribuicao}
                      onChange={(evento) => setContribuicao(evento.target.value)}
                      inputMode="decimal"
                      placeholder={
                        publicacao.contribuicaoSugeridaCentavos
                          ? (publicacao.contribuicaoSugeridaCentavos / 100).toFixed(2).replace('.', ',')
                          : '10,00'
                      }
                      className="h-9 w-32 rounded-md border border-input bg-background px-3 text-sm"
                    />
                  </Campo>
                ) : null}

                <Button onClick={() => void consultar()} disabled={ocupado === 'consultar'}>
                  {ocupado === 'consultar' ? <Loader2 className="animate-spin" /> : <Wallet />}
                  {gratuito ? 'Abrir sem cobrança' : `Consultar por ${precoFormatado(publicacao.precoAPagar)}`}
                </Button>
                <p className="text-[11px] leading-relaxed text-muted-foreground">
                  O pagamento é pelo Pix ou cartão, e o acesso fica liberado para a sua conta.
                  {publicacao.contribuicaoAtiva ? ' A contribuição vai inteira para quem escreveu.' : ''}
                </p>
              </>
            )}
          </CardContent>
        </Card>
      )}

      <p className="rounded-lg border border-border bg-muted/40 p-3 text-xs leading-relaxed text-muted-foreground">
        Publicado por quem escreveu. Declaração de uso de IA: {ROTULO_USO_DE_IA[publicacao.usaIA].toLowerCase()}.
        Se encontrar problema, fale com o desenvolvedor pela área de contato.
      </p>
    </div>
  );
}
